import './style.css'
import * as THREE from 'three'
import { GUI } from 'dat.gui'
import Stats from 'three/examples/jsm/libs/stats.module'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

/**
 * Base
 */
// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Stats
 */
const stats = Stats()
document.body.appendChild(stats.dom)


/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 0
camera.position.y = 0
camera.position.z = 1
scene.add(camera)


/**
 * Lights
 */
const light = new THREE.AmbientLight( 0x404040 ); // soft white light
scene.add( light ); 
const directionalLight = new THREE.DirectionalLight( 0xffffff, 1.0 );
scene.add( directionalLight );
 

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true


/**
 * Geometry
 */
const geometry = new THREE.IcosahedronGeometry( 0.1, 300 );

/**
 * Material
 */
const material = new THREE.MeshStandardMaterial({color: 0x000000})

/*const material = new THREE.PointsMaterial({
    color: 0x000000,
    size: .001
})*/


const customUniforms = {
    uTime: {value: 0},
    uSize: {value: 0.5},
    uSpeed: {value: 0.05},
    uFrequency: {value: 1.037},
    uAmplitude: {value: 3.679},
    uMaxDistance: {value: 1.783},
    uSphere: {value: 0.}
}

material.onBeforeCompile = (shader) => {
    console.log(shader)
    shader.uniforms.uTime = customUniforms.uTime;
    shader.uniforms.uSize = customUniforms.uSize;
    shader.uniforms.uSpeed = customUniforms.uSpeed;
    shader.uniforms.uFrequency = customUniforms.uFrequency;
    shader.uniforms.uAmplitude = customUniforms.uAmplitude;
    shader.uniforms.uMaxDistance = customUniforms.uMaxDistance;
    shader.uniforms.uSphere = customUniforms.uSphere;
    
    // Modify Vertex Shader
    shader.vertexShader = shader.vertexShader.replace(
        '#include <common>',
        `
            #include <common>

            uniform float uTime;
            uniform float uSize;
            uniform float uSpeed;
            uniform float uFrequency;
            uniform float uAmplitude;
            uniform float uMaxDistance;
            uniform float uSphere;

            vec3 mod289(vec3 x) {
                return x - floor(x * (1.0 / 289.0)) * 289.0;
            }
            
            vec2 mod289(vec2 x) {
                return x - floor(x * (1.0 / 289.0)) * 289.0;
            }
            
            vec3 permute(vec3 x) {
                return mod289(((x*34.0)+1.0)*x);
            }
            
            float noise(vec2 v)
            {
                const vec4 C = vec4(0.211324865405187,  // (3.0-sqrt(3.0))/6.0
                                  0.366025403784439,  // 0.5*(sqrt(3.0)-1.0)
                                 -0.577350269189626,  // -1.0 + 2.0 * C.x
                                  0.024390243902439); // 1.0 / 41.0
                // First corner
                vec2 i  = floor(v + dot(v, C.yy) );
                vec2 x0 = v -   i + dot(i, C.xx);
            
                // Other corners
                vec2 i1;
                //i1.x = step( x0.y, x0.x ); // x0.x > x0.y ? 1.0 : 0.0
                //i1.y = 1.0 - i1.x;
                i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
                // x0 = x0 - 0.0 + 0.0 * C.xx ;
                // x1 = x0 - i1 + 1.0 * C.xx ;
                // x2 = x0 - 1.0 + 2.0 * C.xx ;
                vec4 x12 = x0.xyxy + C.xxzz;
                x12.xy -= i1;
            
                // Permutations
                i = mod289(i); // Avoid truncation effects in permutation
                vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
                    + i.x + vec3(0.0, i1.x, 1.0 ));
            
                vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
                m = m*m ;
                m = m*m ;
            
                // Gradients: 41 points uniformly over a line, mapped onto a diamond.
                // The ring size 17*17 = 289 is close to a multiple of 41 (41*7 = 287)
            
                vec3 x = 2.0 * fract(p * C.www) - 1.0;
                vec3 h = abs(x) - 0.5;
                vec3 ox = floor(x + 0.5);
                vec3 a0 = x - ox;
            
                // Normalise gradients implicitly by scaling m
                // Approximation of: m *= inversesqrt( a0*a0 + h*h );
                m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
            
                // Compute final noise value at P
                vec3 g;
                g.x  = a0.x  * x0.x  + h.x  * x0.y;
                g.yz = a0.yz * x12.xz + h.yz * x12.yw;
                return 130.0 * dot(m, g);
            }
            
            vec3 curl(float	x,	float	y,	float	z)
            {
            
                float	eps	= 1., eps2 = 2. * eps;
                float	n1,	n2,	a,	b;
            
                x += uTime * uSpeed;
                y += uTime * uSpeed;
                z += uTime * uSpeed;
            
                vec3	curl = vec3(0.);
            
                n1	=	noise(vec2( x,	y	+	eps ));
                n2	=	noise(vec2( x,	y	-	eps ));
                a	=	(n1	-	n2)/eps2;
            
                n1	=	noise(vec2( x,	z	+	eps));
                n2	=	noise(vec2( x,	z	-	eps));
                b	=	(n1	-	n2)/eps2;
            
                curl.x	=	a	-	b;
            
                n1	=	noise(vec2( y,	z	+	eps));
                n2	=	noise(vec2( y,	z	-	eps));
                a	=	(n1	-	n2)/eps2;
            
                n1	=	noise(vec2( x	+	eps,	z));
                n2	=	noise(vec2( x	+	eps,	z));
                b	=	(n1	-	n2)/eps2;
            
                curl.y	=	a	-	b;
            
                n1	=	noise(vec2( x	+	eps,	y));
                n2	=	noise(vec2( x	-	eps,	y));
                a	=	(n1	-	n2)/eps2;
            
                n1	=	noise(vec2(  y	+	eps,	z));
                n2	=	noise(vec2(  y	-	eps,	z));
                b	=	(n1	-	n2)/eps2;
            
                curl.z	=	a	-	b;
            
                return	curl;
            }
        `
    )
    shader.vertexShader = shader.vertexShader.replace(
        '#include <begin_vertex>',
        `
            #include <begin_vertex>

            vec3 newNormal = normal;

            float f = uFrequency;
            float amplitude = uAmplitude;
            float maxDistance = uMaxDistance;

            vec3 target = position + amplitude * curl(f * transformed.x, f * transformed.y, f * transformed.z);
            float d = length(transformed - target) / maxDistance;
            transformed = mix(position, target, pow(d, uSphere)) * uSize;

            vec4 mvPositions = modelViewMatrix * vec4( transformed, 1.0 );
            gl_Position = projectionMatrix * mvPositions;
        `
    )
}

/**
 * Mesh
 */
const mesh = new THREE.Points(geometry, material)
scene.add(mesh)

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
})
renderer.setSize(sizes.width, sizes.height)
renderer.setClearColor(0xffffff, 1);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * Dat gui
 */
 const gui = new GUI();
 const curlnoisegui = gui.addFolder("Curl Noise");
 curlnoisegui.add(customUniforms.uSize, 'value', 0.05, 2.0).step(0.0001).name("Size");
 curlnoisegui.add(customUniforms.uSpeed, 'value', 0.0001, 0.5).step(0.0001).name("Speed");
 curlnoisegui.add(customUniforms.uFrequency, 'value', 0.7, 7).step(0.001).name("Frequency");
 curlnoisegui.add(customUniforms.uAmplitude, 'value', 0, 4).step(0.001).name("Amplitude");
 curlnoisegui.add(customUniforms.uMaxDistance, 'value', 0, 4).step(0.001).name("Max Distance");
 //curlnoisegui.add(customUniforms.uSphere, 'value', 0, 100).step(0.001).name("Sphere Shape");

/**
 * Animate
 */
const clock = new THREE.Clock()
let lastElapsedTime = 0

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()
    const deltaTime = elapsedTime - lastElapsedTime
    lastElapsedTime = elapsedTime

    // Update material
    customUniforms.uTime.value = elapsedTime

    // Update controls
    controls.update()

    // Update stats
    stats.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()